import React, { useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { Link, useLocation } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import smartlookClient from 'smartlook-client';

import { IconButton, useTheme } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { loadOrg } from '../organization/actions/load-org';
import { userSelector } from '../reducers/current-user-selectors';
import { lastVisitedOrgIdSelector } from '../reducers/display-settings-selectors';
import { orgSelector } from '../reducers/org-selectors';

import { ApplicantSearch } from '../applicant-directory/applicant-search';
import { EventCalendarPopover } from '../event-calendar/event-calendar-popover';
import { OrganizationDropdown } from '../organization/organization-dropdown';
import { ProductTelemetryController } from '../product-telemetry/product-telemetry-controller';
import { headerHeight, mobileHeaderPixels } from '../theme';
import {
  applicantDirectoryRoute,
  eventCalendarRoute,
  HOME_ROUTE,
  jobApplicationPageRoute,
  orgRoute,
} from '../web-routes';

import { isProd } from '../config/config';
import { LOGO_WITH_NAME_WHITE_PATH } from '../config/logo-config';
import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';
import { getTabName } from '../util/navigation-tabs';
import { HIRING_PERMISSION } from '../util/permissions';

const prodGrayOrDevOrange = (theme: any) =>
  isProd ? theme.palette.primary[700] : theme.palette.secondary[900];

const mapStateToProps = (state: any) => {
  const user = userSelector(state);
  const lastVisitedOrgId = user?.username ? lastVisitedOrgIdSelector(state, user.username) : null;
  const org = lastVisitedOrgId ? orgSelector(state, lastVisitedOrgId) : null;

  return {
    org,
    user,
    lastVisitedOrgId,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    loadOrg: (orgId: number) => dispatch(loadOrg(orgId)),
  },
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

export const Nav = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ user, org, lastVisitedOrgId, actions }: Props) => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const history = useHistory();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const calendarAnchorRef = useRef<HTMLDivElement | null>(null);
  const [viewingCalendar, setViewingCalendar] = useState<boolean>(false);

  useEffect(() => {
    if (lastVisitedOrgId && isAuthenticated) {
      actions.loadOrg(lastVisitedOrgId);
    }
  }, [lastVisitedOrgId, isAuthenticated, actions]);

  const handleToggleShowingCalendar = () => {
    // For mobile views, we'll route to the full calendar. Otherwise, show popover
    if (isOnMobile) {
      if (isProd && smartlookClient) {
        smartlookClient.track('Click to Full Calendar View', { source: 'Icon button mobile' });
      }

      ProductTelemetryController.logEvent('full calendar link clicked');

      history.push(eventCalendarRoute(lastVisitedOrgId));
    } else {
      if (isProd && smartlookClient) {
        smartlookClient.track('Click to view Popover Calendar', { source: 'Icon button' });
      }

      ProductTelemetryController.logEvent('popover calendar link clicked');

      setViewingCalendar(!viewingCalendar);
    }
  };

  const path = location.pathname;

  const lastVisitedAccountId = org?.accountId || user?.accountId;
  const hasHiringPermissions = user?.hiringPermission === HIRING_PERMISSION.ALL;
  const isSearching = path === applicantDirectoryRoute();
  // for the application page we don't have the applicant id so we'll just look for /application
  const isViewingSingleApplicant = path.split('/')[1] === jobApplicationPageRoute().split('/')[1];
  const isInAccountMenuContext =
    !!getTabName(path, ['account'], { accountId: lastVisitedAccountId }) && path !== orgRoute();
  const isInAccountListContext = path === HOME_ROUTE && (user?.isAdmin || user?.accountGroupId);
  const canSearch =
    !isSearching &&
    !isInAccountMenuContext &&
    !isInAccountListContext &&
    !isViewingSingleApplicant &&
    hasHiringPermissions;
  const isInOrgContext = org && !isInAccountListContext;

  return (
    <div className="u-width-100" style={{ height: isOnMobile ? mobileHeaderPixels : headerHeight }}>
      <AppBar
        elevation={0}
        color="default"
        className="u-border-bottom u-color-white u-justifyContent-center"
        style={{
          background: prodGrayOrDevOrange(theme),
          height: isOnMobile ? mobileHeaderPixels : headerHeight,
        }}
      >
        <Toolbar className="u-display-flex u-justifyContent-spaceBetween u-no-padding-right">
          <div className="u-display-flex u-alignItems-center">
            <Link to={HOME_ROUTE} className="u-margin-right-lg">
              <img src={LOGO_WITH_NAME_WHITE_PATH} height={isOnMobile ? '30' : '50'} alt="Home" />
            </Link>
          </div>
          <div className="u-display-flex u-alignItems-center">
            {canSearch && (
              <ApplicantSearch path={location.search ? `${path}${location.search}` : path} />
            )}
            {isInOrgContext ? (
              <div className="u-hover-highlight" ref={calendarAnchorRef}>
                <IconButton
                  color="primary"
                  className="u-backgroundHover-transparent u-cursor-pointer u-spread u-padding-lg"
                  size="small"
                  onClick={handleToggleShowingCalendar}
                >
                  <MaterialDesignIcon
                    iconName={ICON_NAMES.CALENDAR_TODAY}
                    color="white"
                    className="u-margin-sm"
                  />
                </IconButton>
                <EventCalendarPopover
                  orgId={lastVisitedOrgId}
                  anchorRef={calendarAnchorRef}
                  showPopover={viewingCalendar}
                  toggleShowPopover={handleToggleShowingCalendar}
                />
              </div>
            ) : null}
            {isInOrgContext ? (
              <div
                className={`u-color-white ${isOnMobile ? '' : 'u-border-left'}`}
                style={{ background: prodGrayOrDevOrange(theme) }}
              >
                <OrganizationDropdown
                  orgId={org.org_id}
                  accountId={org.accountId}
                  isOnMobile={isOnMobile}
                />
              </div>
            ) : null}
          </div>
        </Toolbar>
      </AppBar>
    </div>
  );
});
